import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { geideaOrder, geideaStatus } from '../api';
import { CheckCircle2, XCircle, Loader2, ShieldCheck, CreditCard, Home, ArrowRight, RefreshCw } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

let sdkPromise = null;
const loadSdk = (src) => {
  if (window.GeideaCheckout) return Promise.resolve();
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src; s.async = false;
      s.onload = resolve; s.onerror = () => { sdkPromise = null; reject(new Error('sdk')); };
      document.head.appendChild(s);
    });
  }
  return sdkPromise;
};

const GeideaCheckout = () => {
  const { ref } = useParams();
  const [params] = useSearchParams();
  const { lang, dir } = useLang();
  const ar = lang === 'ar';
  const [order, setOrder] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | ready | paying | verifying | paid | cancelled | failed | error
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState(params.get('order_id') || '');
  const pollRef = useRef(0);

  const verify = useCallback(async (oid) => {
    setPhase('verifying');
    pollRef.current = 0;
    const tick = async () => {
      try {
        const s = await geideaStatus(ref, oid || undefined);
        if (s.status === 'paid') { setOrder((o) => ({ ...o, ...s })); setPhase('paid'); return; }
        if (s.status === 'failed') { setOrder((o) => ({ ...o, ...s })); setPhase('failed'); return; }
        pollRef.current += 1;
        if (pollRef.current < 20) setTimeout(tick, 2000); else setPhase('failed');
      } catch (e) { setError(e?.response?.data?.detail || 'Failed'); setPhase('error'); }
    };
    tick();
  }, [ref]);

  useEffect(() => {
    (async () => {
      try {
        const o = await geideaOrder(ref);
        setOrder(o);
        if (o.status === 'paid') { setPhase('paid'); return; }
        if (orderId) { verify(orderId); return; }
        await loadSdk(o.hpp_script_url);
        setPhase('ready');
      } catch (e) {
        setError(e?.response?.data?.detail || (ar ? 'تعذّر تحميل الطلب' : 'Could not load this payment'));
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const pay = async () => {
    try {
      const o = await geideaOrder(ref); // refreshes an expired session if needed
      setOrder(o);
      await loadSdk(o.hpp_script_url);
      setPhase('paying');
      const onSuccess = (d) => { setOrderId(d?.orderId || ''); verify(d?.orderId); };
      const onError = (d) => { setError(d?.detailedResponseMessage || d?.responseMessage || ''); if (d?.orderId) { setOrderId(d.orderId); verify(d.orderId); } else setPhase('failed'); };
      const onCancel = () => setPhase('cancelled');
      const payment = new window.GeideaCheckout(onSuccess, onError, onCancel);
      payment.startPayment(o.session_id);
    } catch (e) {
      setError(e?.response?.data?.detail || (ar ? 'تعذّر فتح صفحة الدفع' : 'Could not open the payment page'));
      setPhase('error');
    }
  };

  const isSub = order?.kind === 'subscription';
  const nextHref = isSub ? '/accounting' : (order?.meta?.application_id ? '/MyApplications' : '/');

  return (
    <div dir={dir} className="min-h-screen pt-24 pb-16 bg-[#f8fafc] dark:bg-slate-950 flex items-start justify-center px-6">
      <div className="w-full max-w-md" data-testid="geidea-checkout">
        <div className="glass-card rounded-2xl p-8 text-center">
          {order && (
            <div className="mb-6 text-start rounded-xl border border-[#e5e7eb] dark:border-slate-700 bg-white dark:bg-slate-900 p-4" data-testid="geidea-order-summary">
              <div className="text-[10px] uppercase tracking-widest text-[#111827]/50 dark:text-slate-400 font-bold">{ar ? 'تفاصيل الدفع' : 'Payment details'}</div>
              <div className="mt-1 text-sm text-[#111827] dark:text-slate-100">{order.description}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-3xl text-[#2563eb] tabular-nums" data-testid="geidea-amount">{fmt(order.amount)}</span>
                <span className="text-sm text-[#111827]/60 dark:text-slate-400">{order.currency}</span>
              </div>
              <div className="mt-1 text-[10px] font-mono text-[#111827]/40 dark:text-slate-500" data-testid="geidea-ref">{order.ref}</div>
            </div>
          )}

          {phase === 'loading' && <Loader2 className="w-12 h-12 text-[#2563eb] mx-auto animate-spin" />}

          {(phase === 'ready' || phase === 'paying' || phase === 'cancelled') && (
            <>
              {phase === 'cancelled' && (
                <div className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="geidea-cancelled">
                  {ar ? 'تم إلغاء الدفع — لم يُخصم أي مبلغ. يمكنك المحاولة مجدداً.' : 'Payment cancelled — nothing was charged. You can try again.'}
                </div>
              )}
              <Button onClick={pay} disabled={phase === 'paying'} className="gold-button rounded-md h-12 px-6 text-sm font-bold w-full" data-testid="geidea-pay-btn">
                {phase === 'paying' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 me-2" /> {ar ? 'ادفع الآن بالبطاقة' : 'Pay now with card'}</>}
              </Button>
              <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#111827]/50 dark:text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5 text-[#2563eb]" /> {ar ? 'دفع آمن عبر Geidea · Visa / Mastercard · 3-D Secure' : 'Secured by Geidea · Visa / Mastercard · 3-D Secure'}
              </div>
            </>
          )}

          {phase === 'verifying' && (
            <>
              <Loader2 className="w-14 h-14 text-[#2563eb] mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-2xl text-[#0a0a0a] dark:text-white mb-2">{ar ? 'يتم تأكيد الدفع…' : 'Confirming payment…'}</h1>
              <p className="text-sm text-[#111827]/60 dark:text-slate-400">{ar ? 'قد يستغرق هذا بضع ثوانٍ. لا تُغلق النافذة.' : 'This may take a few seconds. Please don\'t close this window.'}</p>
            </>
          )}

          {phase === 'paid' && (
            <>
              <CheckCircle2 className="w-16 h-16 text-blue-600 mx-auto mb-4" data-testid="geidea-paid-icon" />
              <h1 className="font-display text-2xl text-[#0a0a0a] dark:text-white mb-2">{ar ? 'تم الدفع بنجاح!' : 'Payment successful!'}</h1>
              <p className="text-sm text-[#111827]/60 dark:text-slate-400">
                {isSub ? (ar ? 'تم تفعيل اشتراكك. مرحباً بك في iAccounting.' : 'Your subscription is now active. Welcome to iAccounting.')
                       : (ar ? 'تم تحديث حالة الإيصال إلى مدفوع.' : 'The receipt has been marked as paid.')}
              </p>
              {order?.geidea_order_id && <div className="mt-3 text-[10px] font-mono text-[#111827]/45 dark:text-slate-500" data-testid="geidea-order-id">Order ID: {order.geidea_order_id}</div>}
              <Link to={nextHref}>
                <Button className="gold-button rounded-md h-11 px-6 text-sm mt-6" data-testid="geidea-continue-btn">
                  {isSub ? (ar ? 'الذهاب إلى لوحة المحاسبة' : 'Go to accounting dashboard') : (ar ? 'متابعة' : 'Continue')} <ArrowRight className="w-4 h-4 ms-2 rtl:rotate-180" />
                </Button>
              </Link>
            </>
          )}

          {(phase === 'failed' || phase === 'error') && (
            <>
              <XCircle className="w-16 h-16 text-rose-600 mx-auto mb-4" />
              <h1 className="font-display text-2xl text-[#0a0a0a] dark:text-white mb-2">{phase === 'failed' ? (ar ? 'لم تتم عملية الدفع' : 'Payment did not go through') : (ar ? 'حدث خطأ' : 'Something went wrong')}</h1>
              <p className="text-sm text-[#111827]/60 dark:text-slate-400" data-testid="geidea-error">{error || order?.detailed_response || (ar ? 'يرجى المحاولة مرة أخرى أو التواصل مع الدعم.' : 'Please try again or contact support.')}</p>
              {phase === 'failed' && (
                <Button onClick={() => { setError(''); setOrderId(''); pay(); }} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent mt-5" data-testid="geidea-retry-btn">
                  <RefreshCw className="w-3.5 h-3.5 me-1" /> {ar ? 'حاول مرة أخرى' : 'Try again'}
                </Button>
              )}
            </>
          )}

          <div className="mt-6 flex justify-center">
            <Link to="/"><Button className="ghost-button rounded-md h-9 px-4 text-xs bg-transparent"><Home className="w-3.5 h-3.5 me-1" /> {ar ? 'الرئيسية' : 'Home'}</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeideaCheckout;
